<script setup>
import { ref, computed } from "vue";
import AnTooltip from "./AnTooltip.vue";

// series: [{ values:[], color:"#hex", fill:"rgba()"|null, dash:bool, width:number, label:string }]
const props = defineProps({
  series: { type: Array, required: true },
  height: { type: Number, default: 200 },
  // optional label per x-axis point, shown as the tooltip title
  pointLabels: { type: Array, default: () => [] },
});

const W = 600;
const H = 200;
const pad = { t: 12, r: 8, b: 18, l: 8 };

const wrapEl = ref(null);
const hoverIndex = ref(-1);
const mouseX = ref(0);
const mouseY = ref(0);

const pointCount = computed(() => props.series[0]?.values.length || 0);

const maxVal = computed(() =>
  Math.max(...props.series.reduce((a, s) => a.concat(s.values), [])) || 1
);

const gridY = computed(() =>
  [0, 1, 2, 3].map((g) => pad.t + (g / 3) * (H - pad.t - pad.b))
);

const built = computed(() =>
  props.series.map((s) => {
    const n = s.values.length;
    const pts = s.values.map((v, i) => {
      const x = pad.l + (i / (n - 1)) * (W - pad.l - pad.r);
      const y = H - pad.b - (v / maxVal.value) * (H - pad.t - pad.b);
      return [x, y];
    });
    const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
    const last = pts[n - 1];
    return {
      line,
      area: `${line} L${last[0].toFixed(1)} ${H - pad.b} L${pad.l} ${H - pad.b} Z`,
      color: s.color,
      fill: s.fill,
      dash: s.dash ? "4 4" : null,
      width: s.width || 2.4,
      points: pts,
      label: s.label || "",
    };
  })
);

const crosshairX = computed(() => {
  if (hoverIndex.value < 0 || pointCount.value < 2) return null;
  return pad.l + (hoverIndex.value / (pointCount.value - 1)) * (W - pad.l - pad.r);
});

const tooltipRows = computed(() => {
  if (hoverIndex.value < 0) return [];
  return built.value.map((s, i) => ({
    label: s.label || `Series ${i + 1}`,
    value: (props.series[i].values[hoverIndex.value] ?? 0).toLocaleString(),
    color: s.color,
  }));
});

const tooltipTitle = computed(() =>
  hoverIndex.value >= 0 ? props.pointLabels[hoverIndex.value] || "" : "",
);

function onMouseMove(e) {
  if (!wrapEl.value || pointCount.value < 2) return;
  const rect = wrapEl.value.getBoundingClientRect();
  const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  hoverIndex.value = Math.round(frac * (pointCount.value - 1));
  mouseX.value = e.clientX;
  mouseY.value = e.clientY;
}

function onMouseLeave() {
  hoverIndex.value = -1;
}
</script>

<template>
  <div ref="wrapEl" class="an-chart-wrap" @mousemove="onMouseMove" @mouseleave="onMouseLeave">
    <svg class="an-chart" :style="{ height: height + 'px' }" :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none">
      <line
        v-for="(gy, i) in gridY"
        :key="i"
        :x1="pad.l"
        :x2="W - pad.r"
        :y1="gy"
        :y2="gy"
        stroke="var(--an-line)"
        stroke-width="1"
        vector-effect="non-scaling-stroke"
      />
      <template v-for="(s, i) in built" :key="i">
        <path v-if="s.fill" :d="s.area" :fill="s.fill" />
        <path
          :d="s.line"
          fill="none"
          :stroke="s.color"
          :stroke-width="s.width"
          :stroke-dasharray="s.dash"
          vector-effect="non-scaling-stroke"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </template>

      <!-- hover crosshair + per-series dots -->
      <template v-if="crosshairX !== null">
        <line
          :x1="crosshairX"
          :x2="crosshairX"
          :y1="pad.t"
          :y2="H - pad.b"
          stroke="var(--an-muted)"
          stroke-width="1"
          stroke-dasharray="3 3"
          vector-effect="non-scaling-stroke"
        />
        <circle
          v-for="(s, i) in built"
          :key="i"
          :cx="s.points[hoverIndex][0]"
          :cy="s.points[hoverIndex][1]"
          r="3.5"
          :fill="s.color"
          stroke="var(--an-panel)"
          stroke-width="1.5"
        />
      </template>
    </svg>

    <AnTooltip :visible="hoverIndex >= 0" :x="mouseX" :y="mouseY" :title="tooltipTitle" :rows="tooltipRows" />
  </div>
</template>

<style scoped>
.an-chart-wrap {
  position: relative;
}
</style>
